/* eslint-disable camelcase */
import React, { useState, Fragment } from "react";
import PropTypes from "prop-types";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { StateSelection } from "@components/common";
import { PaymentService } from "@services";
import { STRIPE_KEY } from "@constants";
import utils from "@utils";

import "./CreditCardForm.scss";

const Form = ({ userAddress, successCallback, onCancel, onSubmit }) => {
	const stripe = useStripe();
	const elements = useElements();

	const [form, setForm] = useState({
		_state: userAddress._state ?? "",
		name: userAddress.name ?? "",
		address_line1: userAddress.address_line1 ?? "",
		address_city: userAddress.address_city ?? "",
		address_state: userAddress.address_state ?? "",
		address_zip: userAddress.address_zip ?? "",
	});

	const [validations, setValidations] = useState({
		_state: false, // true = has error
		name: false,
		address_line1: false,
		address_city: false,
		address_zip: false,
	});

	const [editAddress, setEditAddress] = useState(false);
	const [loading, setLoading] = useState(false);

	const { lang } = global;

	/**
	 * Makes a request to create a new payment method in the RollKall database
	 * @param {String} TokenId A token representing the payment method
	 */
	const savePaymentMethod = TokenId => {
		PaymentService.addPaymentMethod({ PaymentCard: { TokenId } })
			.then(() => {
				setLoading(false);
				utils.notify({
					type: "success",
					message: lang.t("Payment Method added successfully"),
				});

				if (successCallback) successCallback();
			})
			.catch(({ response }) => {
				setLoading(false);
				utils.notify({
					type: "error",
					message: response?.data?.Message ?? lang.t("Error adding Payment Method"),
				});
			});
	};

	/**
	 * @async
	 * Validates that address details are present before submitting them to Stripe. This creates a payment method token when successful,
	 * which is saved to our database (see {@link savePaymentMethod}).
	 * @param {Event} event The HTML event handled by this method
	 */
	const onSubmitForm = async event => {
		if (event) event.preventDefault();
		if (!stripe || !elements) return;

		// Validate form
		const newValidations = { ...validations };
		newValidations._state = form._state === "";
		newValidations.name = form.name === "";
		newValidations.address_line1 = form.address_line1 === "";
		newValidations.address_city = form.address_city === "";
		newValidations.address_zip = form.address_zip === "";

		// If any validation property is true, the form is not valid
		const validationError = Object.values(newValidations).some(item => item);
		setValidations(newValidations);

		if (validationError) return;

		setLoading(true);

		// Get a reference to a mounted CardElement. Elements knows how to find your CardElement
		// because there can only ever be one of each type of element.
		const cardElement = elements.getElement(CardElement);

		// Add form values, US country and USD currency to stripe request
		const { error, token } = await stripe.createToken(cardElement, {
			...form,
			address_country: "US",
			currency: "USD",
		});

		if (error) {
			setLoading(false);
			utils.notify({
				type: "error",
				message: error.message ?? lang.t("Error getting token"),
			});
		} else if (onSubmit) {
			setLoading(false);
			onSubmit({ type: "creditCard", token });
		} else {
			savePaymentMethod(token.id);
		}
	};

	/** Returns a readable address when the form is collapsed. If any fields are missing, the text returned will be "Incomplete address" instead. */
	const buildSummary = () => {
		const { address_line1, address_city, address_state, address_zip } = form;

		if (address_line1 && address_city && address_state && address_zip) {
			return <p>{`${address_line1}, ${address_city}, ${address_state}, ${address_zip}`}</p>;
		}

		return <p>{lang.t("Incomplete address")}</p>;
	};

	const onFormChange = property => event => {
		const value = event?.target?.value ?? "";

		setForm(prev => ({
			...prev,
			[property]: value,
		}));
	};

	/**
	 * Handles changes to the address state
	 * @param {Event} event The HTML event handled by this method
	 */
	const onStateChange = event => {
		const value = event?.target?.value ?? "";

		// Get label of selected option
		const index = event.nativeEvent.target.selectedIndex;
		const label = event.nativeEvent.target[index].text;

		setForm(prev => ({
			...prev,
			address_state: label.substr(5),
			_state: value,
		}));
	};

	const toggleAddressFields = () => {
		setEditAddress(prev => !prev);
	};

	return (
		<Fragment>
			<div className={`row form-group ${validations.name ? "has-error" : ""}`}>
				<div className="col-xs-12">
					<label htmlFor="name">{lang.t("Card Holder Full Name")}</label>
				</div>
				<div className="col-xs-12">
					<input
						type="text"
						id="name"
						value={form.name}
						onChange={onFormChange("name")}
						className="form-control"
						placeholder="John Doe"
					/>
				</div>
			</div>
			<div className="row form-group">
				<div className="col-xs-12">
					<label htmlFor="CardElement">{lang.t("Card Number")}</label>
				</div>
				<div className="col-xs-12">
					<CardElement
						options={{
							style: {
								base: {
									"::placeholder": {
										color: "#b6cce2",
									},
								},
							},
							hidePostalCode: true,
						}}
					/>
				</div>
			</div>
			<div id="credit-card-separator-dashed" />
			<div
				id="credit-card-billing-address"
				className="row"
			>
				<div className="col-xs-6 text-left">
					<strong>{lang.t("Billing Address")}</strong>
				</div>
				<div className="col-xs-6 text-right">
					<button
						type="button"
						className="btn btn-link"
						onClick={toggleAddressFields}
					>
						<i
							className={`rkf ${editAddress ? "rkf-chevron-up" : "rkf-chevron-down"}`}
						/>
						{lang.t("Edit Address")}
					</button>
				</div>
			</div>
			<div>
				<Choose>
					<When condition={!editAddress}>{buildSummary()}</When>
					<Otherwise>
						<div
							className={`row form-group ${validations.address_line1 ? "has-error" : ""}`}
						>
							<div className="col-xs-12">
								<label htmlFor="address_line1">{lang.t("Address")}</label>
							</div>
							<div className="col-xs-12">
								<input
									type="text"
									id="address_line1"
									value={form.address_line1}
									onChange={onFormChange("address_line1")}
									className="form-control"
									placeholder="2297 Sundown Lane"
								/>
							</div>
						</div>

						<div
							className={`row form-group ${validations.address_city ? "has-error" : ""}`}
						>
							<div className="col-xs-12">
								<label htmlFor="address_city">{lang.t("City")}</label>
							</div>
							<div className="col-xs-12">
								<input
									type="text"
									id="address_city"
									value={form.address_city}
									onChange={onFormChange("address_city")}
									className="form-control"
									placeholder="Austin"
								/>
							</div>
						</div>

						<div className="row">
							<div
								className={`col-xs-6 form-group ${validations._state ? "has-error" : ""}`}
							>
								<div>
									<label htmlFor="address_state">{lang.t("State")}</label>
								</div>
								<StateSelection
									id="address_state"
									name="address_state"
									value={form._state}
									onChange={onStateChange}
									className="form-control"
								/>
							</div>

							<div
								className={`col-xs-6 form-group ${validations.address_zip ? "has-error" : ""}`}
							>
								<div>
									<label htmlFor="address_zip">{lang.t("Zip Code")}</label>
								</div>
								<input
									type="text"
									id="address_zip"
									value={form.address_zip}
									onChange={onFormChange("address_zip")}
									className="form-control"
									placeholder="78749"
								/>
							</div>
						</div>
					</Otherwise>
				</Choose>
			</div>

			<div className="row">
				<div
					className="col-xs-12"
					id="credit-card-btn-container"
				>
					<button
						type="button"
						className="btn btn-link"
						onClick={onCancel}
					>
						{lang.t("Cancel")}
					</button>
					<button
						type="button"
						disabled={!stripe || loading}
						onClick={onSubmitForm}
						className="btn btn-primary"
					>
						{loading ? lang.t("Loading") : lang.t("Save")}
					</button>
				</div>
			</div>
		</Fragment>
	);
};

Form.propTypes = {
	userAddress: PropTypes.object,
	successCallback: PropTypes.func,
	onCancel: PropTypes.func,
	onSubmit: PropTypes.func,
};

Form.defaultProps = {
	userAddress: {},
	successCallback: undefined,
	onCancel: undefined,
	onSubmit: undefined,
};

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(STRIPE_KEY);

/* eslint-disable react/jsx-props-no-spreading */
const CreditCardForm = props => (
	<div id="credit-card-form">
		<Elements stripe={stripePromise}>
			<Form {...props} />
		</Elements>
	</div>
);

export default CreditCardForm;
